import React from 'react';
import { useQuery } from '@apollo/client/react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import withLayout from 'hocs/withLayout';
import MySubmissions from './MySubmissions';
import { ROUTES } from 'config/Nav';
import { MY_SUBMISSIONS } from 'lib/graphql/queries/submissions';
import { useRouter } from 'next/router';
import { Loading } from '@elevator/ui';
import { Submission } from 'types/submissions';

/**
 * MySubmissionsWithData Component
 *
 * Wrapper component that fetches submission data and handles loading, error, and empty states.
 * Provides the data to the MySubmissions component for display.
 *
 * Features:
 * - Fetches user submissions via GraphQL query
 * - Handles loading state with Loading component
 * - Displays error state with retry functionality
 * - Shows empty state when user has no submissions
 * - Scrolls to top on mount
 *
 * Used in:
 * - MySubmissions layout index file (wrapped with `withLayout`, `md` width)
 */
const MySubmissionsWithData = () => {
  const theme = useTheme();
  const router = useRouter();
  const mobile = useMediaQuery(theme.breakpoints.down('md'), { noSsr: true });

  // TODO navigate if not signed in
  React.useEffect(() => {
    window && window.scrollTo(0, 0);
  }, []);

  const { data, loading, networkStatus, error, refetch } = useQuery<{
    mySubmissions: Submission[];
  }>(MY_SUBMISSIONS, {
    notifyOnNetworkStatusChange: true,
  });

  const submissions = data && data.mySubmissions ? data.mySubmissions : [];

  const isLoading = loading || networkStatus === 4;

  const onRetryQueryClick = () => {
    refetch();
  };

  const onStartNewSubmission = () => {
    router.push(ROUTES.SUBMIT.to);
  };

  const renderErrorState = () => {
    return (
      <Box
        minHeight={'70vh'}
        display={'flex'}
        flexDirection={'column'}
        alignItems={'center'}
        justifyContent={'center'}
      >
        <Typography variant={'h5'} gutterBottom>
          Oh snap!
        </Typography>
        <Box maxWidth={mobile ? '80%' : '50%'}>
          <Typography
            variant={'body1'}
            align={'center'}
            gutterBottom
            color={'textSecondary'}
          >
            Something went wrong while retrieving your submissions.
          </Typography>
        </Box>
        <Box paddingTop={2}>
          <Button variant={'text'} color={'primary'} onClick={onRetryQueryClick}>
            Retry
          </Button>
        </Box>
      </Box>
    );
  };

  const renderEmptyState = () => {
    return (
      <Box
        minHeight={'70vh'}
        display={'flex'}
        flexDirection={'column'}
        alignItems={'center'}
        justifyContent={'center'}
      >
        <Typography variant={'h5'} gutterBottom>
          No Submissions
        </Typography>
        <Box maxWidth={mobile ? '80%' : '50%'}>
          <Typography
            variant={'body1'}
            color={'textSecondary'}
            align={'center'}
            gutterBottom
          >
            Your submissions will appear here any time you make one. Need to start a new
            submission? Click the button below!
          </Typography>
        </Box>
        <Box paddingTop={2}>
          <Button variant={'text'} color={'primary'} onClick={onStartNewSubmission}>
            Start a new submission
          </Button>
        </Box>
      </Box>
    );
  };

  const renderContent = () => {
    if (isLoading) {
      return <Loading />;
    }

    if (error) {
      return renderErrorState();
    }

    if (submissions.length < 1) {
      return renderEmptyState();
    }

    return <MySubmissions submissions={submissions} />;
  };

  return (
    <Box width="100%" data-layout="my-submissions">
      {renderContent()}
    </Box>
  );
};

/** `md` column: 940px max (`SITE_LAYOUT.layoutContentMd`), with top margin. */
export default withLayout(MySubmissionsWithData, {
  contentWidth: 'md',
  margin: true,
});
